import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { Plan, PLAN_NAMES } from '@/types/plan';
import { useOrganization } from './OrganizationContext';
import { planService } from '@/services/planService';
import { useAuth } from '@clerk/react';

interface PlanContextType {
  plans: Plan[];
  currentPlan: Plan | null;
  freePlan: Plan | null;
  isLoading: boolean;
  getPlanById: (id: string) => Plan | undefined;
  getPlanByName: (name: string) => Plan | undefined;
  // Limit checking helpers
  canAddMember: () => boolean;
  getRemainingMemberSlots: () => number | null; // null = unlimited
  isAtCpuLimit: (currentCpuUsage: number) => boolean;
  isAtStorageLimit: (currentStorageGb: number) => boolean;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentOrganization, members } = useOrganization();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isLoaded: authLoaded, isSignedIn } = useAuth();

  // Load plans from the API (planService falls back to the seeded localStorage
  // plans only when VITE_ENABLE_MOCK_FALLBACK is on and the request fails).
  // Signed in only: /plans/ needs a session, so a signed-out page (sign-in, sign-up) asking for it
  // just got a 401. Loads again after a sign-in in the same page (2026-10-08).
  useEffect(() => {
    if (!authLoaded || !isSignedIn) return;
    let cancelled = false;
    const loadPlans = async () => {
      setIsLoading(true);
      try {
        const apiPlans = await planService.getPlans();
        if (!cancelled) setPlans(apiPlans);
      } catch (error) {
        console.error('Failed to load plans:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [authLoaded, isSignedIn]);

  const getPlanById = (id: string): Plan | undefined => {
    return plans.find(p => p.id === id);
  };

  const getPlanByName = (name: string): Plan | undefined => {
    return plans.find(p => p.name === name);
  };

  const currentPlanId = currentOrganization?.planId;
  const currentPlan = useMemo(
    () => (currentPlanId ? plans.find((p) => p.id === currentPlanId) ?? null : null),
    [currentPlanId, plans]);

  const freePlan = useMemo(() => plans.find((p) => p.name === PLAN_NAMES.FREE) ?? null, [plans]);

  // An unresolved plan (still loading, or a row the API didn't return) must not read as
  // "no seats left" — the API enforces the real limits; treat unknown as unlimited.
  const canAddMember = (): boolean => {
    if (!currentPlan) return true;
    if (currentPlan.maxMembers === null) return true; // Unlimited
    return members.length < currentPlan.maxMembers;
  };

  const getRemainingMemberSlots = (): number | null => {
    if (!currentPlan) return null;
    if (currentPlan.maxMembers === null) return null; // Unlimited
    return Math.max(0, currentPlan.maxMembers - members.length);
  };

  const isAtCpuLimit = (currentCpuUsage: number): boolean => {
    if (!currentPlan) return false;
    if (currentPlan.maxCpuQuota === null) return false; // Unlimited
    return currentCpuUsage >= currentPlan.maxCpuQuota;
  };

  const isAtStorageLimit = (currentStorageGb: number): boolean => {
    if (!currentPlan) return true;
    if (currentPlan.maxStorageGb === null) return false; // Unlimited
    return currentStorageGb >= currentPlan.maxStorageGb;
  };

  const value: PlanContextType = {
    plans,
    currentPlan,
    freePlan,
    isLoading,
    getPlanById,
    getPlanByName,
    canAddMember,
    getRemainingMemberSlots,
    isAtCpuLimit,
    isAtStorageLimit,
  };

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};
